import { useCollectionServices } from '../../../contexts/CollectionSourceContext';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import Refresh from '@mui/icons-material/Refresh';
import { FitbitExtractionJob } from '../../../services/fitbitExtractionService';
import FitbitExtractionDownload from './FitbitExtractionDownload';
import FitbitExtractionCloudLink from './FitbitExtractionCloudLink';
import FitbitExtractionProgress, { extractionFileSize, extractionStatus } from './FitbitExtractionProgress';
import { metricPresentation } from '../collection/metrics/metricPresentation';
import { storedDateLabel } from './StoredDateRange';

const timestamp = (value?: string | null) => value ? new Date(value).toLocaleString() : '—';
const measurement = (routeKey: string) => metricPresentation({ dataType: routeKey.split('.')[0], routeKey });

export default function FitbitExtractionDetails({ studyId, initialJob, onClose }: {
  studyId: string; initialJob: FitbitExtractionJob; onClose: () => void;
}) {
  const { getFitbitExtraction } = useCollectionServices();
  const [extraction, setExtraction] = useState(initialJob);
  const [participantId, setParticipantId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      setLoading(true);
      try {
        const result = await getFitbitExtraction(studyId, initialJob.jobId);
        if (!alive) return;
        setExtraction(result.job); setError('');
        setParticipantId(previous => result.job.participants?.some(item => item.participantId === previous)
          ? previous : result.job.participants?.[0]?.participantId || '');
        if (result.job.state === 'queued' || result.job.state === 'running') timer = setTimeout(refresh, 5000);
      } catch { if (alive) setError('Could not refresh job details. The last saved status is shown.'); }
      finally { if (alive) setLoading(false); }
    };
    void refresh();
    return () => { alive = false; clearTimeout(timer); };
  }, [studyId, initialJob.jobId, revision, getFitbitExtraction]);
  const grouped = extraction.kind === 'batch';
  const participants = extraction.participants || [];
  const job = grouped ? participants.find(item => item.participantId === participantId) || participants[0] : extraction;
  return <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="extraction-details-title"
    PaperProps={{ sx: { borderRadius: '16px', m: { xs: 1.5, sm: 3 }, width: 'calc(100% - 24px)', maxHeight: 'calc(100dvh - 32px)' } }}>
    <Box sx={{ position: 'relative' }}>
      <DialogTitle id="extraction-details-title" sx={{ pr: 7 }}>Extraction details</DialogTitle>
      <IconButton aria-label="Close extraction details" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><Close /></IconButton>
    </Box>
    <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fafafb' }}>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {grouped && <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography fontWeight={650} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{extraction.name}</Typography>
          <Chip size="small" label={extractionStatus(extraction)} color={extraction.state === 'completed' ? 'success' : extraction.state === 'failed' ? 'error' : 'default'} />
        </Stack>
        <Typography variant="body2" color="text.secondary">{extraction.participantCount} participants · One extraction, separate files</Typography>
        <FitbitExtractionProgress job={extraction} />
        {extraction.error && <Alert severity="warning" sx={{ mt: 1.5 }}>{extraction.error}</Alert>}
        {!!job && <TextField select fullWidth size="small" label="Participant" value={job.participantId}
          onChange={event => setParticipantId(event.target.value)} sx={{ mt: 2.5 }}>
          {participants.map(item => <MenuItem key={item.participantId} value={item.participantId}>
            {item.participantName || item.participantId}{item.trackingId ? ` · ${item.trackingId}` : ''}
          </MenuItem>)}
        </TextField>}
        {!job && loading && <CircularProgress size={24} aria-label="Loading participant files" sx={{ mt: 2 }} />}
      </Box>}
      {job && <Box sx={grouped ? { p: { xs: 1.5, sm: 2.5 }, border: '1px solid #e7e8ec', borderRadius: '12px', bgcolor: 'white' } : undefined}>
        <ParticipantFileDetails job={job} />
      </Box>}
    </DialogContent>
    <DialogActions sx={{ px: 2.5, py: 2, justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Button size="small" startIcon={<Refresh />} disabled={loading} onClick={() => setRevision(value => value + 1)}>Refresh details</Button>
      {job && extraction.state !== 'deleting' && <FitbitExtractionCloudLink job={job} />}
      {job && extraction.state !== 'deleting' && <FitbitExtractionDownload key={job.jobId} studyId={studyId} job={job} />}
    </DialogActions>
  </Dialog>;
}

function ParticipantFileDetails({ job }: { job: FitbitExtractionJob }) {
  const routes = job.routeKeys === undefined ? (job.routeKey ? [job.routeKey] : null) : job.routeKeys;
  const fields = [
    ...(job.participantName ? [['Participant name', job.participantName]] : []),
    [job.trackingId ? 'Tracking ID' : 'Participant', job.trackingId || job.participantId], ['File format', { json: 'JSON (.json)', ndjson: 'JSON Lines (.ndjson)', csv: 'CSV (.csv)', zip: 'Research tables (.zip)' }[job.format]],
    ...(job.cloudFolder ? [['Google Cloud folder', job.cloudFolder]] : []),
    ...(job.cloudFile ? [['Google Cloud file', job.cloudFile]] : []),
    ...(job.template ? [['Template', `${job.template.name} · version ${job.template.version}`]] : []),
    ['File size', extractionFileSize(job.byteCount)], ['Created', timestamp(job.createdAt)],
    ...(job.completedAt ? [['Completed', timestamp(job.completedAt)]] : []),
  ];
  return <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography fontWeight={650} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{job.fileName || `${job.name}.${job.format}`}</Typography>
        <Chip size="small" label={extractionStatus(job)} color={job.state === 'completed' ? 'success' : job.state === 'failed' ? 'error' : 'default'} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Fitbit · {storedDateLabel(job.startDate)} – {storedDateLabel(job.endDate)}</Typography>
      <FitbitExtractionProgress job={job} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Timezone for participant: {job.dateBasis === 'recorded_local' ? 'Local' : 'UTC'}</Typography>
      <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
        Progress follows the requested dates across selected measurements. 100% means the file is saved and ready to download.
      </Typography>
      {job.state === 'running' && job.progress?.routeKey && <Typography variant="body2" sx={{ mt: 1 }}>
        Processing {measurement(job.progress.routeKey).label}{job.progress.throughDate ? ` · Date reached: ${storedDateLabel(job.progress.throughDate)}` : ''}
      </Typography>}
      {job.error && <Alert severity="error" sx={{ mt: 2 }}>{job.error}</Alert>}
      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, my: 2.5 }}>
        {fields.map(([label, value]) => <Box key={label} sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{value}</Typography>
        </Box>)}
      </Box>
      <Typography variant="caption" color="text.secondary">Measurements</Typography>
      <Stack direction="row" flexWrap="wrap" gap={.75} sx={{ mt: .75, maxHeight: 160, overflowY: 'auto' }}>
        {routes === null ? <Chip size="small" label="All measurements" /> : routes.map(routeKey => {
          const info = measurement(routeKey);
          return <Chip key={routeKey} size="small" label={info.label} sx={{ bgcolor: info.background, color: info.color, maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' }, height: 'auto', minHeight: 24 }} />;
        })}
      </Stack>
  </>;
}
